import { ClipboardList, Moon, BarChart3, type LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface RoutineStep {
  prompt: string;
}

export interface Routine {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  estimatedCredits: string;
  steps: RoutineStep[];
}

export const ROUTINES: Routine[] = [
  {
    id: 'morning',
    icon: ClipboardList,
    title: 'Ouverture boutique',
    description: 'Stock critique, ventes d\'hier, objectifs du jour',
    estimatedCredits: '~3 crédits',
    steps: [
      { prompt: 'Quels produits sont en rupture ou en alerte stock ce matin ?' },
      { prompt: 'Résume les ventes d\'hier avec les points forts et les risques.' },
      { prompt: 'Propose-moi 3 objectifs réalistes pour aujourd\'hui.' },
    ],
  },
  {
    id: 'closing',
    icon: Moon,
    title: 'Clôture journée',
    description: 'CA du jour, top produits, rapport PDF',
    estimatedCredits: '~4 crédits',
    steps: [
      { prompt: 'Quel est mon chiffre d\'affaires du jour et comment il se compare à la moyenne ?' },
      { prompt: 'Quels sont les produits les plus vendus aujourd\'hui ?' },
      { prompt: 'Génère un rapport PDF complet de la journée.' },
    ],
  },
  {
    id: 'weekly',
    icon: BarChart3,
    title: 'Bilan hebdomadaire',
    description: 'Tendances, performance employés, actions',
    estimatedCredits: '~5 crédits',
    steps: [
      { prompt: 'Compare mes ventes de cette semaine avec la semaine dernière.' },
      { prompt: 'Quelle est la performance de chaque employé cette semaine ?' },
      { prompt: 'Donne-moi 3 actions prioritaires pour la semaine prochaine.' },
    ],
  },
];

interface ChatRoutinesProps {
  onSelectRoutine: (routine: Routine) => void;
  className?: string;
}

export function ChatRoutines({ onSelectRoutine, className }: ChatRoutinesProps) {
  return (
    <div className={cn('w-full max-w-[320px]', className)}>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Routines
      </p>
      <div className="flex flex-col gap-2">
        {ROUTINES.map((routine, i) => {
          const Icon = routine.icon;
          return (
            <motion.button
              key={routine.id}
              type="button"
              onClick={() => onSelectRoutine(routine)}
              className="flex items-start gap-3 rounded-xl border border-charcoal-100 dark:border-charcoal-700 bg-muted/50 p-3 text-left active:scale-[0.98] transition-transform hover:bg-muted"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.08, duration: 0.2 }}
            >
              <Icon className="mt-0.5 size-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-charcoal-800 dark:text-charcoal-200">
                  {routine.title}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground leading-tight">
                  {routine.description}
                </p>
              </div>
              <span className="shrink-0 text-[10px] font-medium text-amber-500">
                {routine.estimatedCredits}
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
